import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config';
import { CashbackEntry } from '../../../types';
import { useState, useEffect } from 'react';

const COLLECTION_NAME = 'cashbacks';

const toCashback = (d: any): CashbackEntry => {
  const data = d.data();
  return {
    id: d.id,
    providerName: data.providerName || '',
    totalAmount: data.totalAmount || 0,
    cases: (data.cases || []).map((c: any) => ({
      caseId: c.caseId || '',
      caseNumber: c.caseNumber,
      caseName: c.caseName || '',
      amount: c.amount || 0,
    })),
    receiptUrl: data.receiptUrl || '',
    receiptName: data.receiptName || '',
    date: data.date?.toDate() || new Date(),
    notes: data.notes || '',
    status: data.status || 'pending',
    createdBy: data.createdBy || '',
    createdByName: data.createdByName || '',
    createdAt: data.createdAt?.toDate() || new Date(),
    adminId: data.adminId,
    adminNotes: data.adminNotes,
    adminDecisionAt: data.adminDecisionAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
  };
};

export const subscribeCashbacks = (callback: (cashbacks: CashbackEntry[]) => void) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(toCashback));
  });
};

export const addCashback = async (
  entry: Omit<CashbackEntry, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const data: any = {
    ...entry,
    date: entry.date instanceof Date ? Timestamp.fromDate(entry.date) : entry.date,
    adminDecisionAt: entry.adminDecisionAt instanceof Date ? Timestamp.fromDate(entry.adminDecisionAt) : entry.adminDecisionAt,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  // Firestore rejects undefined — strip them out
  Object.keys(data).forEach(k => { if (data[k] === undefined) delete data[k]; });
  const docRef = await addDoc(collection(db, COLLECTION_NAME), data);
  return docRef.id;
};

export const updateCashback = async (
  id: string,
  updates: Partial<Omit<CashbackEntry, 'id' | 'createdAt'>>
): Promise<void> => {
  const data: any = { ...updates, updatedAt: Timestamp.now() };
  if (data.date instanceof Date) data.date = Timestamp.fromDate(data.date);
  if (data.adminDecisionAt instanceof Date) data.adminDecisionAt = Timestamp.fromDate(data.adminDecisionAt);
  await updateDoc(doc(db, COLLECTION_NAME, id), data);
};

export const deleteCashback = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, COLLECTION_NAME, id));
};

export const useCashbacks = () => {
  const [cashbacks, setCashbacks] = useState<CashbackEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeCashbacks((items) => {
      setCashbacks(items);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  return { cashbacks, loading };
};
